import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  SetStateAction,
  Dispatch,
} from 'react';
import MultiCarousel from 'react-multi-carousel';
import { useMedia } from 'react-use';

import FavoriteIcon from 'components/FavoriteIcon';
import VehicleBadges from 'components/VehicleBadges';
import { Badge } from 'components/VehicleBadges/types';
import Counter from 'components/Shared/Counter';
import { screen } from 'components/styles/variables';

import useEvents from 'lib/hooks/useEvents';
import { Features } from 'lib/EventSchema';
import imageTapEvent from 'lib/Tracking/Vdp/PhotoCarousel/ImageTapEvent';
import favoriteToggleEvent from 'lib/Tracking/Vdp/PhotoCarousel/FavoriteToggleEvent';
import VdpEventContext from 'contexts/VdpEventContext';

import ShareIcon from 'components/ShareIcon';
import useOptimizelyDecisions from 'lib/hooks/useOptimizelyDecisions';
import navigatorShare from 'lib/navigatorShare';
import Thumbnail from '../Thumbnail';
import CarouselButtons from '../CarouselButtons';
import ThumbnailButtons from '../ThumbnailButtons';
import styles, { heartCss, counterCss, sliderCss } from './styles';
import Dots from '../Dots';

interface Props {
  images: string[];
  onImageClick(): void;
  isFavorited: boolean;
  favorite(): void;
  unfavorite(): void;
  onSlideChange?: (index: number) => void;
  badges: Badge[];
  lightboxIndex: number | null;
  slideIndex: number;
  setLightboxIndex: (num: number | null) => void;
  setSlideIndex: Dispatch<SetStateAction<number>>;
  vehicleInfo: {
    year: number;
    make: string;
    model: string;
    trim: string | null;
    vin: string;
  };
}

// Required, ensures that one primary image is shown across all breakpoints
const responsive = {
  all: {
    breakpoint: { max: 4000, min: 0 },
    items: 1,
  },
};

export default function PhotoCarousel({
  images,
  onImageClick,
  isFavorited,
  favorite,
  unfavorite,
  onSlideChange = (): void => {},
  badges,
  lightboxIndex,
  setLightboxIndex,
  slideIndex,
  setSlideIndex,
  vehicleInfo,
}: Props): JSX.Element {
  const [slidePage, setSlidePage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const logger = useEvents();
  const { trackingParams } = useContext(VdpEventContext);
  const isMobile = useMedia(`(max-width: ${screen.xsMax})`, false);
  const { getDecision } = useOptimizelyDecisions();
  const smsFunctionality = getDecision('smsFunctionality');

  const { year, make, model, trim, vin } = vehicleInfo || {};
  const title = `${year} ${make} ${model} ${trim}`;

  function onShareClick(): void {
    navigatorShare({
      title,
      text: 'Check out this vehicle on Autolist!',
      url: `https://www.autolist.com/listings/${vin}`,
    });
  }

  useEffect(() => {
    setPageCount(Math.ceil(images.length / 4) - 1);
  }, [images]);

  const goBackOnePage = useCallback(() => {
    setSlidePage((prev: number): number => {
      return prev - 1;
    });
  }, [setSlidePage]);

  const goForwardOnePage = useCallback(() => {
    setSlidePage((prev: number): number => {
      return prev + 1;
    });
  }, [setSlidePage]);

  return (
    <div className="sizer">
      <div className="vdp-carousel-wrapper">
        <div className="placemat" /> {/* this provides quick-to-load LCP element for mobile */}
        <MultiCarousel
          swipeable={true}
          beforeChange={(): void => setIsTransitioning(true)}
          afterChange={(previousSlide, { currentSlide }): void => {
            setIsTransitioning(false);
            if (isMobile) {
              // handle swiping from last image to first
              if (previousSlide === images.length + 1) {
                setSlideIndex(0);
              } else if (currentSlide < 2) {
                // handle swiping between last two images
                // for first image, react-multi-carousel sets currentSlide as 2
                setSlideIndex(currentSlide ? images.length - 1 : images.length - 2);
              } else if (Math.abs(currentSlide - previousSlide) === 1) {
                // handle navigation back or forward 1 image
                // currentSlide - 2 will be calculated as slideIndex +- 1
                setSlideIndex(currentSlide - 2);
              }
            }
          }}
          draggable={false}
          showDots={true}
          responsive={responsive}
          ssr={true} // means to render carousel on server-side.
          infinite={true}
          keyBoardControl={true}
          customTransition="all .5"
          transitionDuration={0}
          containerClass="carousel-container"
          removeArrowOnDeviceType={['tablet', 'mobile']}
          dotListClass="dot-list"
          itemClass={`image-slide ${sliderCss.className}`}
          customDot={
            // index, active, and onClick are passed into Thumbnail automatically
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <Thumbnail
              slidePage={slidePage}
              setSlideIndex={setSlideIndex}
              onSlideChange={onSlideChange}
              images={images}
            />
          }
          customButtonGroup={
            // next and previous are passed into CarouselButtons automatically
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <CarouselButtons
              slideIndex={slideIndex}
              setSlideIndex={setSlideIndex}
              onSlideChange={onSlideChange}
              images={images}
              lightboxIndex={lightboxIndex}
              setLightboxIndex={setLightboxIndex}
              setSlidePage={setSlidePage}
              isTransitioning={isTransitioning}
              goBackOnePage={goBackOnePage}
              goForwardOnePage={goForwardOnePage}
            />
          }
          renderButtonGroupOutside={false}
          renderDotsOutside={true}
          partialVisible={false}
          partialVisbile={false}
        >
          {images.map((image) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              data-testid="image"
              loading="lazy"
              className={`image-slide-image`}
              src={image}
              alt="vehicle-image-slide"
              onClick={(): void => {
                logger.log(imageTapEvent({ feature: Features.vehicleCarousel, trackingParams }));
                onImageClick();
              }}
              key={image}
            />
          ))}
        </MultiCarousel>
        <div className="dots-container">
          <Dots count={images.length} currentSlide={slideIndex} />
        </div>
        {smsFunctionality && isMobile && (
          <ShareIcon data-testid="share-icon" onClick={onShareClick} />
        )}
        <FavoriteIcon
          data-testid="heart"
          className={heartCss.className}
          active={isFavorited}
          onClick={(): void => {
            logger.log(favoriteToggleEvent({ isFavorited, trackingParams }));
            isFavorited ? unfavorite() : favorite();
          }}
        />
        <Counter
          className={`counter ${counterCss.className}`}
          count={images.length}
          index={slideIndex}
        />
        <ThumbnailButtons pageCount={pageCount} slidePage={slidePage} setSlidePage={setSlidePage} />
        <div className="vehicle-badges">
          <VehicleBadges badges={badges} />
        </div>
      </div>
      {sliderCss.styles}
      {heartCss.styles}
      {counterCss.styles}
      <style>{styles}</style>
    </div>
  );
}
